import React, { useState, useEffect } from 'react';
import {
    Box, Button, Checkbox, CssBaseline, Divider,
    FormControlLabel, Grid, Step, StepLabel, Stepper,
    Typography, TextField, InputAdornment, IconButton,
    CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
    GoogleAuthProvider,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    createUserWithEmailAndPassword,
    sendEmailVerification,
    signOut,
} from 'firebase/auth';
import { firebaseAuth, getErrorMessage } from '../../firebase';
import { useHistory, useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import AutocompleteAddress from '../../components/AutocompleteAddress';
import MapContainer from '../../components/MapContainer';
import AdvancedMarker from '../../components/AdvancedMarker';
import MenuItem from '@mui/material/MenuItem';
import { genderCode } from '../../constants/genderCode';
import PhonePrefixesSelect from '../../components/VendorComponents/PhonePrefixesSelect';
import TextInput from '../../components/forms/TextInput';
import { getAddressComponents } from '../../services/FormBuilderManager';
import { post, put, get, APP_USER, CITIES, CONNECT, USERS } from '../../services/Client';
import { CONNECT_SESSION_SERVICE, CONNECT_SESSION_CODE } from '../Connect/ConnectPage';
import { BrandLogo } from '../../components/pages/LoginPage';
import { BRANDS, getBrand } from '../../branding';

// Stessa impaginazione del login: sfondo grigio, tutto dentro una card bianca.
const PAGE_BG = '#f4f5f7';
const BORDER = '#e5e7eb';

function PageCard({ brand, children }) {
    return (
        <Box component="main" sx={{
            minHeight: '100vh', bgcolor: PAGE_BG, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'center', px: 2, py: { xs: 3, sm: 6 },
        }}>
            <CssBaseline />
            <Box sx={{
                width: '100%', maxWidth: 560, bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '16px',
                px: { xs: 3, sm: 5 }, py: { xs: 4, sm: 5 },
                // "&&" per vincere sugli stili di TextInput (angoli a zero, bordo scuro), che hanno la stessa specificita'
                '&& .MuiOutlinedInput-root': { borderRadius: '10px' },
                '&& .MuiOutlinedInput-root:not(.Mui-error) fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)', borderWidth: '1px' },
                '&& .MuiOutlinedInput-root:not(.Mui-error):hover fieldset': { borderColor: 'rgba(0, 0, 0, 0.87)' },
                '&& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) fieldset': { borderColor: brand.color, borderWidth: '2px' },
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <BrandLogo brand={brand} height={brand.logoHeight.login} />
                </Box>
                {children}
                <Typography variant="caption" component="div" align="center" sx={{ mt: 4, color: '#9ca3af' }}>
                    © {new Date().getFullYear()} {brand.owner}
                </Typography>
            </Box>
        </Box>
    );
}

const isInAppBrowser = () => {
    const ua = navigator.userAgent || '';
    return /FBAN|FBAV|Instagram|Telegram|Line|MicroMessenger/i.test(ua) || /; wv\)/.test(ua);
};


const GOOGLE_ICON = (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
        <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
        <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
        <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
        <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
);

export default function NewPinbiker() {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const brand = getBrand();
    const brandButtonSx = {
        borderRadius: '10px', textTransform: 'uppercase', fontWeight: 600, boxShadow: 'none',
        bgcolor: brand.color, color: brand.contrastText,
        '&:hover': { bgcolor: brand.color, filter: 'brightness(0.92)', boxShadow: 'none' },
    };
    const outlinedButtonSx = { borderRadius: '10px', textTransform: 'uppercase', color: 'text.primary', borderColor: BORDER };
    const linkSx = { color: brand.color, fontWeight: 600, textDecoration: 'none' };

    const urlParams = new URLSearchParams(location.search);
    const [connectService] = useState(urlParams.get('service') || sessionStorage.getItem(CONNECT_SESSION_SERVICE));
    const [connectCode] = useState(urlParams.get('code') || sessionStorage.getItem(CONNECT_SESSION_CODE));

    const [step, setStep] = useState(0);
    const [authMethod, setAuthMethod] = useState(null); // 'google' | 'email'
    const [loading, setLoading] = useState(false);
    const [connectSuccess, setConnectSuccess] = useState(false);
    const [data, setData] = useState({
        phoneNumberPrefix: '+39',
        genderCode: 0,
    });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [cities, setCities] = useState([]);
    const [signupIncomplete, setSignupIncomplete] = useState(false);

    useEffect(() => {
        get(CITIES).then(c => setCities(c || [])).catch(() => {});
    }, []);

    // Se l'utente è già autenticato in Firebase al mount (ritorno da signInWithRedirect
    // oppure signup incompleto), verifica lo stato nel backend e gestisci di conseguenza
    useEffect(() => {
        const currentUser = firebaseAuth.currentUser;
        if (!currentUser) return;

        get(USERS, { elem: currentUser.uid })
            .then(userData => {
                if (userData.signupCompleted) {
                    // Utente già registrato → vai alla home
                    window.location.href = '/';
                } else {
                    // signupCompleted = false → riprendi la registrazione dal profilo
                    setSignupIncomplete(true);
                    const nameParts = (currentUser.displayName || '').split(' ');
                    setData(prev => ({
                        ...prev,
                        email: userData.email || currentUser.email || '',
                        firstName: userData.firstName || nameParts[0] || '',
                        lastName: userData.lastName || nameParts.slice(1).join(' ') || '',
                        avatarUrl: userData.avatarUrl || currentUser.photoURL || '',
                        phoneNumberPrefix: userData.phoneNumberPrefix || '+39',
                        genderCode: userData.genderCode ?? 0,
                    }));
                    setAuthMethod('google');
                    setStep(1);
                }
            })
            .catch(() => {
                // Utente non nel backend → nuovo utente che torna dal redirect Google
                const nameParts = (currentUser.displayName || '').split(' ');
                setData(prev => ({
                    ...prev,
                    email: currentUser.email || '',
                    firstName: nameParts[0] || '',
                    lastName: nameParts.slice(1).join(' ') || '',
                    avatarUrl: currentUser.photoURL || '',
                }));
                setAuthMethod('google');
                setStep(1);
            });
    }, []);

    // Gestione errori da signInWithRedirect (mobile)
    useEffect(() => {
        getRedirectResult(firebaseAuth).catch(e => {
            const msg = getErrorMessage(e);
            if (msg) enqueueSnackbar(msg, { variant: 'error' });
        });
    }, []);

    const steps = [
        t('authStep'),
        t('profileStep'),
        t('addressStep'),
        t('termsStep'),
    ];

    const handleChange = (field, value) => {
        setData(prev => ({ ...prev, [field]: value }));
        setErrors(prev => ({ ...prev, [field]: null }));
    };

    // ─── Step 0: Google SSO ────────────────────────────────────────────────
    async function handleGoogleSignIn() {
        if (isInAppBrowser()) {
            enqueueSnackbar(t('openInBrowser'), { variant: 'warning', autoHideDuration: 6000 });
            return;
        }
        setLoading(true);
        setErrors({});
        const provider = new GoogleAuthProvider();
        try {
            const result = await signInWithPopup(firebaseAuth, provider);
            const fbUser = result.user;

            // Verifica se l'utente è già registrato in Pinbike
            try {
                await get(APP_USER);
                window.location.href = '/';
                return;
            } catch (_) {
                // utente non ancora registrato, continua
            }

            const nameParts = (fbUser.displayName || '').split(' ');
            setData(prev => ({
                ...prev,
                email: fbUser.email || '',
                firstName: nameParts[0] || '',
                lastName: nameParts.slice(1).join(' ') || '',
                avatarUrl: fbUser.photoURL || '',
            }));
            setAuthMethod('google');
            setStep(1);
        } catch (e) {
            if (['auth/popup-blocked', 'auth/popup-closed-by-user', 'auth/cancelled-popup-request'].includes(e.code)) {
                // Popup non disponibile → fallback redirect; al ritorno il useEffect currentUser gestisce
                await signInWithRedirect(firebaseAuth, provider);
            } else {
                const msg = getErrorMessage(e);
                if (msg) enqueueSnackbar(msg, { variant: 'error' });
            }
        }
        setLoading(false);
    }

    // ─── Step 0: email/password → avanza al passo 1 ───────────────────────
    function handleEmailNext() {
        const newErrors = {};
        if (!data.email) newErrors.email = t('mandatoryField');
        if (!data.newPassword) newErrors.newPassword = t('mandatoryField');
        if (!data.confirmPassword) newErrors.confirmPassword = t('mandatoryField');
        if (data.newPassword && data.confirmPassword && data.newPassword !== data.confirmPassword) {
            newErrors.confirmPassword = t('passwordNotMatch');
        }
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        setAuthMethod('email');
        setStep(1);
    }

    // ─── Step 1: validazione dati personali ───────────────────────────────
    function validateProfile() {
        const newErrors = {};
        if (!data.firstName) newErrors.firstName = t('mandatoryField');
        if (!data.lastName) newErrors.lastName = t('mandatoryField');
        if (!data.username) newErrors.username = t('mandatoryField');
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    // ─── Step 2: selezione indirizzo con Google Places ────────────────────
    async function onAddressSelect(address, placeId) {
        if (!address || !placeId) {
            handleChange('homeAddress', null);
            return;
        }
        try {
            const place = new window.google.maps.places.Place({ id: placeId });
            await place.fetchFields({ fields: ['location', 'addressComponents'] });
            if (!place.location) return;
            const ac = getAddressComponents(place);
            const locality = ac('locality') || ac('political') || ac('administrative_area_level_3');

            // Cerca la city nel backend per ottenere l'istatId (richiesto da JPA)
            const matchedCity = cities.find(c =>
                c.city && c.city.toLowerCase() === (locality || '').toLowerCase()
            ) || null;

            handleChange('homeAddress', {
                address: ac('route'),
                number: ac('street_number'),
                locality,
                city: matchedCity
                    ? { istatId: matchedCity.istatId, city: matchedCity.city, province: matchedCity.province }
                    : null,
                latitude: place.location.lat(),
                longitude: place.location.lng(),
            });
        } catch (e) {
            console.error(e);
            setErrors(prev => ({ ...prev, homeAddress: t('internalServerError') }));
        }
    }

    // ─── Navigazione avanti ────────────────────────────────────────────────
    const handleNext = () => {
        if (step === 1 && !validateProfile()) return;
        setStep(s => s + 1);
    };

    // ─── Submit finale: POST /app/user poi PUT /app/user/{uid} ────────────
    async function handleSubmit() {
        if (!data.termsAccepted) {
            setErrors({ termsAccepted: t('mandatoryField') });
            return;
        }
        setLoading(true);
        let uid;
        try {
            // 1. Autenticazione Firebase (solo per flusso email)
            if (authMethod === 'email') {
                const { user: newUser } = await createUserWithEmailAndPassword(
                    firebaseAuth, data.email, data.newPassword
                );
                uid = newUser.uid;
                // Invia subito la mail di verifica prima che checkUser possa fare signOut
                await sendEmailVerification(newUser, { url: window.location.origin + '/emailValidated' });
            } else {
                uid = firebaseAuth.currentUser.uid;
            }

            // 2. POST /app/user → crea l'utente nel backend con uid + email
            // (skip se signupIncomplete: il record esiste già)
            if (!signupIncomplete) {
                await post(APP_USER, { body: { uid, email: data.email } });
            }

            // 3. PUT /app/user/{uid} → aggiorna con tutti i dati del profilo
            await put(APP_USER, {
                body: {
                    firstName: data.firstName || null,
                    lastName: data.lastName || null,
                    username: data.username || null,
                    genderCode: data.genderCode ?? null,
                    birthDate: data.birthDate || null,
                    phoneNumberPrefix: data.phoneNumberPrefix || null,
                    phoneNumber: data.phoneNumber || null,
                    homeAddress: data.homeAddress || null,
                    avatarUrl: data.avatarUrl || null,
                    termsAccepted: true,
                    marketingTermsAccepted: data.marketingTermsAccepted || false,
                    invitationCode: data.invitationCode || null,
                    signupCompleted: true,
                },
            });
        
            // 4. Connect + Redirect
            if (connectService && connectCode) {
                try {
                    await post(CONNECT, { body: { service: connectService, code: connectCode } });
                } catch (_) {}
                sessionStorage.removeItem(CONNECT_SESSION_SERVICE);
                sessionStorage.removeItem(CONNECT_SESSION_CODE);
                if (authMethod === 'email') await signOut(firebaseAuth);
                setConnectSuccess(true);

            } else if (authMethod === 'email') {
                await signOut(firebaseAuth);
                history.push('/confirmEmail');
                
            } else {
                window.location.href = '/';
            }
            
        } catch (e) {
            console.error(e);
            // Rollback Firebase user se il backend ha fallito (solo flusso email)
            if (authMethod === 'email' && firebaseAuth.currentUser) {
                await firebaseAuth.currentUser.delete().catch(() => {});
            }
            enqueueSnackbar(getErrorMessage(e), { variant: 'error' });
        }
        setLoading(false);
    }

    // ─── Render step 0: scelta metodo auth ────────────────────────────────
    const renderStep0 = () => (
        <Box>
            <Button
                fullWidth
                variant="outlined"
                onClick={handleGoogleSignIn}
                disabled={loading}
                startIcon={GOOGLE_ICON}
                sx={{ ...outlinedButtonSx, textTransform: 'none', mb: 2, py: 1.25, fontSize: '1rem' }}
            >
                {t('continueWithGoogle')}
            </Button>

            <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="textSecondary">{t('or')}</Typography>
            </Divider>

            <TextField
                fullWidth variant="outlined" label={t('email')} type="email"
                value={data.email || ''}
                onChange={e => handleChange('email', e.target.value)}
                error={!!errors.email} helperText={errors.email}
                sx={{ mb: 2 }} autoComplete="email"
            />
            <TextField
                fullWidth variant="outlined" label={t('password')}
                type={showPassword ? 'text' : 'password'}
                value={data.newPassword || ''}
                onChange={e => handleChange('newPassword', e.target.value)}
                error={!!errors.newPassword} helperText={errors.newPassword}
                sx={{ mb: 2 }} autoComplete="new-password"
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(p => !p)} edge="end" size="large">
                                {showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                        </InputAdornment>
                    )
                }}
            />
            <TextField
                fullWidth variant="outlined" label={t('confirmPassword')}
                type={showPassword ? 'text' : 'password'}
                value={data.confirmPassword || ''}
                onChange={e => handleChange('confirmPassword', e.target.value)}
                error={!!errors.confirmPassword} helperText={errors.confirmPassword}
                sx={{ mb: 2 }} autoComplete="new-password"
            />

            {errors.auth && (
                <Typography color="error" align="center" variant="body2" sx={{ mb: 2 }}>
                    {errors.auth}
                </Typography>
            )}

            <Button
                fullWidth variant="contained" disableElevation
                onClick={handleEmailNext} disabled={loading}
                sx={{ ...brandButtonSx, py: 1.25, fontSize: '1rem' }}
            >
                {loading ? <CircularProgress size={24} color="inherit" /> : t('forward')}
            </Button>
        </Box>
    );

    // ─── Render step 1: dati personali ───────────────────────────────────
    const renderStep1 = () => (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextInput label={t('firstName')} value={data.firstName || ''}
                    onTextChange={v => handleChange('firstName', v)}
                    error={errors.firstName} required color="primary" />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextInput label={t('lastName')} value={data.lastName || ''}
                    onTextChange={v => handleChange('lastName', v)}
                    error={errors.lastName} required color="primary" />
            </Grid>
            <Grid item xs={12}>
                <TextInput label={t('username')} value={data.username || ''}
                    onTextChange={v => handleChange('username', v)}
                    error={errors.username} required color="primary" />
            </Grid>
            <Grid item xs={12}>
                {/* non GenderSelect: il suo contenitore ha un padding fisso che lo disallinea dagli altri campi */}
                <TextInput select label={t('gender')} value={data.genderCode}
                    onTextChange={v => handleChange('genderCode', v)} color="primary">
                    {Object.values(genderCode).map(o => <MenuItem key={o.id || -1} value={o.id}>{t(o.value)}</MenuItem>)}
                </TextInput>
            </Grid>
            <Grid item xs={12}>
                <TextInput label={t('birthDate')} type="date" value={data.birthDate || ''}
                    onTextChange={v => handleChange('birthDate', v)}
                    color="primary" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={4}>
                <PhonePrefixesSelect prefix={data.phoneNumberPrefix}
                    onChange={v => handleChange('phoneNumberPrefix', v)} />
            </Grid>
            <Grid item xs={8}>
                <TextInput label={t('phoneNumber')} value={data.phoneNumber || ''}
                    onTextChange={v => handleChange('phoneNumber', v)} color="primary" />
            </Grid>
            <Grid item xs={12}>
                <TextInput label={t('invitationCode')} value={data.invitationCode || ''}
                    onTextChange={v => handleChange('invitationCode', v)} color="primary" />
            </Grid>
        </Grid>
    );

    // ─── Render step 2: indirizzo con mappa ──────────────────────────────
    const renderStep2 = () => (
        <Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {t('homeAddressSubtitle')}
            </Typography>
            <AutocompleteAddress
                label={t('searchAddress')}
                onAddressInputChange={() => {}}
                onAddressSelect={onAddressSelect}
                error={errors.homeAddress}
            />
            {data.homeAddress && (
                <>
                    <Box sx={{ height: 280, mt: 2, borderRadius: 2, overflow: 'hidden' }}>
                        <MapContainer
                            center={{ lat: data.homeAddress.latitude, lng: data.homeAddress.longitude }}
                            zoom={15}
                        >
                            <AdvancedMarker
                                position={{ lat: data.homeAddress.latitude, lng: data.homeAddress.longitude }}
                            />
                        </MapContainer>
                    </Box>
                    <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="body2">
                            {[data.homeAddress.address, data.homeAddress.number, data.homeAddress.locality]
                                .filter(Boolean).join(', ')}
                        </Typography>
                    </Box>
                </>
            )}
        </Box>
    );

    // ─── Render step 3: termini ───────────────────────────────────────────
    const renderStep3 = () => (
        <Box>
            <FormControlLabel
                control={
                    <Checkbox checked={!!data.termsAccepted} color="primary"
                        onChange={e => handleChange('termsAccepted', e.target.checked)} />
                }
                label={
                    <Typography variant="body2">
                        {t('acceptTerms')}{' '}
                        <a href="https://pin.bike/privacy" target="_blank" rel="noopener noreferrer">
                            {t('termsConditionsPage')}
                        </a>
                    </Typography>
                }
                sx={{ alignItems: 'flex-start', mt: 1 }}
            />
            {errors.termsAccepted && (
                <Typography color="error" variant="caption" display="block" sx={{ ml: 4, mb: 1 }}>
                    {errors.termsAccepted}
                </Typography>
            )}
            <FormControlLabel
                control={
                    <Checkbox checked={!!data.marketingTermsAccepted} color="primary"
                        onChange={e => handleChange('marketingTermsAccepted', e.target.checked)} />
                }
                label={<Typography variant="body2">{t('marketingConsent')}</Typography>}
                sx={{ alignItems: 'flex-start', mt: 1 }}
            />
        </Box>
    );

    const renderStepContent = () => {
        switch (step) {
            case 0: return renderStep0();
            case 1: return renderStep1();
            case 2: return renderStep2();
            case 3: return renderStep3();
            default: return null;
        }
    };

    if (connectSuccess) {
        return (
            <PageCard brand={brand}>
                <Box sx={{ textAlign: 'center' }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                        {t('connectSuccess')}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        {t('connectSuccessDescription', { service: connectService })}
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {t('connectDownloadAppMessage')}
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        href="https://www.pin.bike/app-download.php"
                        target="_blank"
                        rel="noopener noreferrer"
                        disableElevation
                        sx={{ ...brandButtonSx, px: 4 }}
                    >
                        {t('connectDownloadApp')}
                    </Button>
                </Box>
            </PageCard>
        );
    }

    return (
        <PageCard brand={brand}>
            <Typography component="h1" variant="h5" align="center" sx={{ fontWeight: 700, mb: 3 }}>
                {t('registerAsPinbiker')}
            </Typography>

            <Stepper activeStep={step} sx={{ mb: 3 }} alternativeLabel>
                {steps.map(label => (
                    <Step key={label}><StepLabel>{label}</StepLabel></Step>
                ))}
            </Stepper>

            {renderStepContent()}

            {step > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                    <Button variant="outlined" onClick={() => setStep(s => s - 1)}
                        disabled={loading} sx={outlinedButtonSx}>
                        {t('backward')}
                    </Button>
                    {step < 3 ? (
                        <Button variant="contained" disableElevation
                            onClick={handleNext} disabled={loading} sx={{ ...brandButtonSx }}>
                            {t('forward')}
                        </Button>
                    ) : (
                        <Button variant="contained" disableElevation
                            onClick={handleSubmit} disabled={loading}
                            sx={{ ...brandButtonSx, minWidth: 130 }}>
                            {loading
                                ? <CircularProgress size={22} color="inherit" />
                                : t('registerHere')}
                        </Button>
                    )}
                </Box>
            )}

            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3 }}>
                {t('alreadyRegistered')}{' '}
                <Link to="/login" style={linkSx}>{t('loginToRegister')}</Link>
            </Typography>
            {/* Solo prima di scegliere Google o email: dopo, l'utente Firebase puo' gia' esistere.
                L'area esercenti e' di Pin Bike: con gli altri marchi il link non c'e'. */}
            {step === 0 && brand.key === BRANDS.pinbike.key && (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                    <Link to="/newVendor" style={linkSx}>{t('registerAsVendor')}</Link>
                </Typography>
            )}
        </PageCard>
    );
}
